<template>
  <div class="page-container">
    <div class="page-title-container flex items-center">
      <wz-button variant="light" class="mr-3" @click="$router.push({ name: 'medical-leave-list' })">
        <i class="ri-arrow-left-line"></i> Volver
      </wz-button>
      <h1 class="page-title mb-0">Carga masiva de descansos médicos</h1>
    </div>

    <!-- Stepper -->
    <div class="flex justify-center mb-4">
      <div class="flex items-center">
        <div :class="['bulk-step', step >= 1 ? 'bulk-step-active' : '']">
          <span class="bulk-step-number">1</span>
          <span class="bulk-step-label">Subir</span>
        </div>
        <div class="bulk-step-line"></div>
        <div :class="['bulk-step', step >= 2 ? 'bulk-step-active' : '']">
          <span class="bulk-step-number">2</span>
          <span class="bulk-step-label">Validar</span>
        </div>
        <div class="bulk-step-line"></div>
        <div :class="['bulk-step', step >= 3 ? 'bulk-step-active' : '']">
          <span class="bulk-step-number">3</span>
          <span class="bulk-step-label">Importar</span>
        </div>
      </div>
    </div>

    <!-- PASO 1 -->
    <div v-if="step === 1">
      <wz-card>
        <div class="flex justify-between items-center mb-4">
          <p class="text-muted mb-0">Importe descansos médicos desde una plantilla Excel (.xlsx)</p>
          <wz-button variant="light" @click="downloadTemplate" :disabled="downloadingTemplate">
            <i class="ri-download-line mr-1"></i>
            {{ downloadingTemplate ? 'Descargando...' : 'Descargar plantilla' }}
          </wz-button>
        </div>

        <div
          class="bulk-dropzone text-center"
          @click="$refs.fileInput.click()"
          @dragover.prevent
          @drop.prevent="onDrop"
        >
          <i class="ri-upload-cloud-line" style="font-size: 3rem; color: #adb5bd;"></i>
          <p class="mt-2 mb-1">Arrastre un archivo .xlsx aquí o haga clic para seleccionar</p>
          <small class="text-muted">Formato: .xlsx | Máximo: 500 filas | Tamaño máximo: 5 MB</small>
        </div>
        <input type="file" ref="fileInput" accept=".xlsx" style="display: none;" @change="onFileSelected" />

        <div v-if="selectedFile" class="mt-3 flex items-center justify-between border rounded p-3">
          <div>
            <i class="ri-file-excel-line text-success mr-2" style="font-size: 1.5rem;"></i>
            <strong>{{ selectedFile.name }}</strong>
            <small class="text-muted ml-2">({{ formatFileSize(selectedFile.size) }})</small>
          </div>
          <div>
            <wz-button variant="light" size="sm" class="mr-2" @click="clearFile">Quitar</wz-button>
            <wz-button variant="primary" @click="processFile" :disabled="processing">
              {{ processing ? 'Procesando...' : 'Procesar archivo' }}
            </wz-button>
          </div>
        </div>

        <wz-alert variant="info" icon="ri-information-line" class="mt-4">
          Los colaboradores deben existir y tener un contrato vigente en la empresa indicada. El canje CITT es
          opcional para descansos PARTICULAR y no debe informarse para ESSALUD.
        </wz-alert>
      </wz-card>
    </div>

    <!-- PASO 2 -->
    <div v-if="step === 2 && validationResult">
      <div class="flex flex-wrap mb-4 -mx-2">
        <div class="w-full md:w-1/3 px-2">
          <div class="bg-white border rounded shadow-sm p-3 text-center">
            <div style="font-size: 2rem; font-weight: 700;">{{ validationResult.totalRows }}</div>
            <div class="text-muted text-sm">Total de filas</div>
          </div>
        </div>
        <div class="w-full md:w-1/3 px-2">
          <div class="bg-white border rounded shadow-sm p-3 text-center">
            <div style="font-size: 2rem; font-weight: 700; color: #28a745;">{{ validationResult.validRows }}</div>
            <div class="text-muted text-sm">Filas válidas</div>
          </div>
        </div>
        <div class="w-full md:w-1/3 px-2">
          <div class="bg-white border rounded shadow-sm p-3 text-center">
            <div style="font-size: 2rem; font-weight: 700; color: #dc3545;">{{ validationResult.errorRows }}</div>
            <div class="text-muted text-sm">Filas con errores</div>
          </div>
        </div>
      </div>

      <wz-card :body-padding="false">
        <template #header-title>Detalle de validación</template>
        <template #header-actions>
          <label class="mb-0 flex items-center text-sm">
            <wz-checkbox v-model="showErrorsFirst" switch size="sm" class="mr-1" />
            Mostrar errores primero
          </label>
        </template>
        <div class="overflow-x-auto">
          <table class="wz-table w-full px-2">
            <thead>
              <tr>
                <th style="width: 50px;">#</th>
                <th style="width: 60px;">Estado</th>
                <th>DNI</th>
                <th>Empresa</th>
                <th>Tipo descanso</th>
                <th>Atención</th>
                <th>Inicio</th>
                <th>Fin</th>
                <th>Errores / Avisos</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in paginatedRows" :key="row.rowNumber"
                  :class="{ 'bulk-row-error': !row.valid }">
                <td>{{ row.rowNumber - 1 }}</td>
                <td class="text-center">
                  <i v-if="row.valid" class="ri-check-line text-success" style="font-size: 1.2rem;"></i>
                  <i v-else class="ri-close-line text-danger" style="font-size: 1.2rem;"></i>
                </td>
                <td>{{ row.dni || '-' }}</td>
                <td>{{ row.companyName || '-' }}</td>
                <td>{{ row.leaveType || '-' }}</td>
                <td>{{ row.careType || '-' }}</td>
                <td>{{ row.startDate || '-' }}</td>
                <td>{{ row.endDate || '-' }}</td>
                <td>
                  <ul v-if="row.errors && row.errors.length" class="mb-0 pl-3 text-danger text-sm">
                    <li v-for="(err, idx) in row.errors" :key="'e'+idx">{{ err }}</li>
                  </ul>
                  <ul v-if="row.warnings && row.warnings.length" class="mb-0 pl-3 text-warning text-sm">
                    <li v-for="(w, idx) in row.warnings" :key="'w'+idx">⚠ {{ w }}</li>
                  </ul>
                  <span v-if="row.valid && (!row.warnings || !row.warnings.length)" class="text-success text-sm">OK</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="totalPages > 1" class="flex justify-center items-center py-3">
          <button type="button" class="button-tooltip" :disabled="previewPage === 1" @click="previewPage--">
            <i class="ri-arrow-left-s-line"></i>
          </button>
          <span class="mx-3 text-sm text-muted">Página {{ previewPage }} de {{ totalPages }}</span>
          <button type="button" class="button-tooltip" :disabled="previewPage === totalPages" @click="previewPage++">
            <i class="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </wz-card>

      <div class="flex justify-between mt-4">
        <wz-button variant="light" @click="cancelImport">Cancelar</wz-button>
        <div>
          <wz-button v-if="validationResult.errorRows > 0"
                  variant="light" class="text-danger mr-2" @click="downloadErrors">
            <i class="ri-download-line mr-1"></i>Descargar errores
          </wz-button>
          <wz-button variant="primary"
                  :disabled="validationResult.validRows === 0 || confirming"
                  @click="askConfirmImport">
            {{ confirming ? 'Importando...' : `Importar válidos (${validationResult.validRows})` }}
          </wz-button>
        </div>
      </div>
    </div>

    <!-- PASO 3 -->
    <div v-if="step === 3">
      <wz-card :body-padding="false">
        <div class="p-5 text-center">
          <template v-if="importResult">
            <i class="ri-checkbox-circle-line text-success" style="font-size: 4rem;"></i>
            <h4 class="mt-3">{{ importResult.message }}</h4>
            <p v-if="importResult.errors > 0" class="text-muted">
              ({{ importResult.errors }} fila(s) no se importaron por errores)
            </p>
            <div class="mt-4">
              <wz-button variant="primary" class="mr-2" @click="$router.push({ name: 'medical-leave-list' })">
                Ir a la lista de descansos médicos
              </wz-button>
              <wz-button variant="light" @click="resetAll">
                Cargar otro archivo
              </wz-button>
            </div>
          </template>
          <template v-else>
            <i class="ri-error-warning-line text-danger" style="font-size: 4rem;"></i>
            <h4 class="mt-3 text-danger">Error al importar</h4>
            <p class="text-muted">{{ importError }}</p>
            <wz-button variant="primary" class="mt-3" @click="resetAll">Intentar de nuevo</wz-button>
          </template>
        </div>
      </wz-card>
    </div>

    <wz-confirm-modal
      v-model="confirmImportModal"
      title="Confirmar importación"
      :description="`¿Está seguro? Se crearán ${validationResult ? validationResult.validRows : 0} descansos médicos.`"
      accept-text="Importar"
      accept-variant="primary"
      alert-variant="info"
      @confirm="confirmImport"
    />
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { MedicalLeaveBulkUploadService } from '@/core/services'
import fileDownload from 'js-file-download'

const PAGE_SIZE = 20

export default {
  name: 'MedicalLeaveBulkUploadPage',
  data() {
    return {
      step: 1,
      selectedFile: null,
      processing: false,
      confirming: false,
      downloadingTemplate: false,
      validationResult: null,
      importResult: null,
      importError: null,
      showErrorsFirst: true,
      previewPage: 1,
      confirmImportModal: false,
    }
  },
  computed: {
    sortedRows() {
      if (!this.validationResult) return []
      const rows = [...this.validationResult.rows]
      if (this.showErrorsFirst) {
        rows.sort((a, b) => {
          if (a.valid === b.valid) return a.rowNumber - b.rowNumber
          return a.valid ? 1 : -1
        })
      }
      return rows
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.sortedRows.length / PAGE_SIZE))
    },
    paginatedRows() {
      const start = (this.previewPage - 1) * PAGE_SIZE
      return this.sortedRows.slice(start, start + PAGE_SIZE)
    },
  },
  watch: {
    showErrorsFirst() {
      this.previewPage = 1
    },
  },
  methods: {
    async downloadTemplate() {
      try {
        this.downloadingTemplate = true
        const resp = await MedicalLeaveBulkUploadService.downloadTemplate()
        fileDownload(resp.data || resp, 'plantilla-descansos-medicos.xlsx')
      } catch (e) {
        Alert.error('Error al descargar la plantilla')
      } finally {
        this.downloadingTemplate = false
      }
    },
    onFileSelected(event) {
      const file = event.target.files[0]
      if (file) this.selectedFile = file
    },
    onDrop(event) {
      const file = event.dataTransfer.files[0]
      if (file && file.name.endsWith('.xlsx')) {
        this.selectedFile = file
      } else {
        Alert.error('Solo se aceptan archivos .xlsx')
      }
    },
    clearFile() {
      this.selectedFile = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    async processFile() {
      if (!this.selectedFile) return
      if (this.selectedFile.size > 5 * 1024 * 1024) {
        Alert.error('El archivo no debe superar 5 MB')
        return
      }
      try {
        this.processing = true
        this.$store.dispatch('app/loading', true)
        const resp = await MedicalLeaveBulkUploadService.validate(this.selectedFile)
        this.validationResult = resp.payload
        this.previewPage = 1
        this.step = 2
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al procesar el archivo')
      } finally {
        this.processing = false
        this.$store.dispatch('app/loading', false)
      }
    },
    askConfirmImport() {
      this.confirmImportModal = true
    },
    async confirmImport() {
      this.confirmImportModal = false
      try {
        this.confirming = true
        this.$store.dispatch('app/loading', true)
        const resp = await MedicalLeaveBulkUploadService.confirm(this.validationResult.batchId)
        this.importResult = resp.payload
        this.step = 3
      } catch (e) {
        this.importError = e.errors ? e.errors.message : 'Error inesperado al importar'
        this.importResult = null
        this.step = 3
      } finally {
        this.confirming = false
        this.$store.dispatch('app/loading', false)
      }
    },
    async downloadErrors() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await MedicalLeaveBulkUploadService.downloadErrors(this.validationResult.batchId)
        fileDownload(resp.data || resp, 'errores-carga-descansos.xlsx')
      } catch (e) {
        Alert.error('Error al descargar el reporte de errores')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    cancelImport() {
      this.resetAll()
    },
    resetAll() {
      this.step = 1
      this.selectedFile = null
      this.validationResult = null
      this.importResult = null
      this.importError = null
      this.previewPage = 1
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },
  },
}
</script>

<style scoped>
.bulk-dropzone {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  padding: 40px 20px;
  cursor: pointer;
  transition: border-color 0.2s;
}
.bulk-dropzone:hover {
  border-color: #007bff;
}
.bulk-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0.4;
}
.bulk-step-active {
  opacity: 1;
}
.bulk-step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #007bff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
}
.bulk-step-label {
  font-size: 12px;
  margin-top: 4px;
  color: #333;
}
.bulk-step-line {
  width: 60px;
  height: 2px;
  background: #dee2e6;
  margin: 0 8px;
  margin-bottom: 18px;
}
.bulk-row-error {
  background-color: #fde8e8 !important;
}
</style>
